import { forwardRef, useId, type InputHTMLAttributes } from 'react'
import { IconeAlerta } from './Icones'

export interface OpcaoDeEscolha {
  valor: string
  rotulo: string
  /** Uma linha sob a opção, quando o rótulo sozinho não diz o efeito dela no indicador. */
  ajuda?: string
}

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  legenda: string
  ajuda?: string
  erro?: string
  opcoes: OpcaoDeEscolha[]
}

/**
 * Escolha única entre poucas opções, em botões de rádio.
 *
 * **Rádio e não `<select>`**, para as perguntas do julgamento da fiscalização: as opções são duas
 * ou três e cada uma precisa de uma frase inteira para ficar clara para quem não é de TI —
 * "Manutenção corretiva — conserto de defeito em produção" não cabe numa lista suspensa sem virar
 * jargão. E as alternativas ficam todas visíveis, então quem responde vê o que está descartando.
 *
 * **Rádio e não caixas**, porque é exatamente o que impede o estado proibido: a tabela recusa
 * "corretiva e não corretiva" e "aceite integral e parcial" por regra do banco, e com caixas
 * independentes o usuário produz o par inválido e recebe um erro de integridade sem tradução.
 *
 * Sem valor pré-marcado quando o formulário abre vazio: em julgamento de fiscalização, não
 * responder e responder "não" são coisas diferentes.
 *
 * **`forwardRef` não é detalhe.** O `register` do formulário devolve `{name, onChange, onBlur,
 * ref}`, e o React descarta a `ref` de componente de função que não a encaminha — sem aviso na
 * tela. O sintoma é cruel: os botões marcam ao clique, e o formulário insiste que nenhuma pergunta
 * foi respondida. A mesma razão pela qual todo campo de `campos.tsx` encaminha a sua.
 *
 * @param props legenda, ajuda, erro, opções e os atributos que o `register` do formulário injeta
 * @returns o grupo de escolha única
 */
export const GrupoDeEscolha = forwardRef<HTMLInputElement, Props>(function GrupoDeEscolha(
  { legenda, ajuda, erro, opcoes, ...resto },
  ref,
) {
  const base = useId()
  const idDaAjuda = `${base}-ajuda`
  const idDoErro = `${base}-erro`
  const descritores = [ajuda ? idDaAjuda : null, erro ? idDoErro : null].filter(Boolean).join(' ')

  return (
    <fieldset className="min-w-0" aria-describedby={descritores || undefined}>
      <legend className="text-sm font-medium text-slate-800">{legenda}</legend>
      {ajuda && (
        <p id={idDaAjuda} className="mt-0.5 max-w-prose text-xs text-slate-500">
          {ajuda}
        </p>
      )}
      <div className="mt-2 space-y-1.5">
        {opcoes.map((opcao) => (
          <div key={opcao.valor} className="flex items-start gap-2">
            <input
              {...resto}
              ref={ref}
              id={`${base}-${opcao.valor}`}
              type="radio"
              value={opcao.valor}
              aria-invalid={erro ? true : undefined}
              aria-describedby={opcao.ajuda ? `${base}-${opcao.valor}-ajuda` : undefined}
              className="mt-0.5 h-4 w-4 shrink-0 border-slate-300 text-niteroi-700 focus:ring-niteroi-600"
            />
            <div className="min-w-0">
              <label htmlFor={`${base}-${opcao.valor}`} className="text-sm text-slate-800">
                {opcao.rotulo}
              </label>
              {opcao.ajuda && (
                <p id={`${base}-${opcao.valor}-ajuda`} className="max-w-prose text-xs text-slate-500">
                  {opcao.ajuda}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
      {erro && (
        <p id={idDoErro} className="mt-1 flex items-start gap-1 text-xs font-medium text-red-700">
          <IconeAlerta className="mt-px h-3.5 w-3.5" />
          <span>{erro}</span>
        </p>
      )}
    </fieldset>
  )
})
